<script lang="ts">
import {Vue, Component, Watch} from 'vue-facing-decorator';
import UserService from "@/services/user.service";
import {useUserStore} from "@/stores/userStore";
import {Application, User} from "@/types";
import ActionButton from "@/components/ActionButton.vue";
import {useDisplay} from "vuetify";
import AdminService from "@/services/admin.service";
@Component({ components: { ActionButton }})
export default class QuicksightAccessManagement extends Vue {
  private mdAndUp = useDisplay().mdAndUp;
  private loading = false;
  private users: User[] = [];
  private quicksightUsers: User[] = [];
  private selectedUsers: User[] = [];
  private selectedUser: User | null = null;
  private headers: Array<{ title: string; value: string; sortable: boolean; width?: string }> = [
    { title: 'Name', value: 'full_name', sortable: true },
    { title: 'Email', value: 'email', sortable: true },
    { title: 'Status', value: 'status', sortable: true }
  ];
  private searchNames = '';
  private async mounted() {
    await this.fetchQuicksightUsers();
    await this.fetchUsers();
  }

  private async fetchQuicksightUsers() {
    this.quicksightUsers = await AdminService.getQuicksightAccess();
  }

  private async fetchUsers() {
    this.users = await AdminService.getAllUsers();
  }

  private async addQuicksightUser() {
    this.loading = true;
    try {
      if (!this.selectedUser || !this.selectedUser.id) {
        return;
      }
      await AdminService.addQuicksightUser(this.selectedUser.id);
    } finally {
      this.loading = false;
      await this.fetchQuicksightUsers();
    }
  }

  private async removeQuicksightUser() {
    if (this.selectedUsers.length) {
      for (const user of this.selectedUsers) {
        if (user.id) {
          await AdminService.removeQuicksightUser(user.id);
        }
      }
      this.selectedUsers = [];
      await this.fetchQuicksightUsers();
    }
  }

  private initials(first: string, last: string): string {
    return `${first.charAt(0)} ${last.charAt(0)}`
  }

  private fullName(first: string, last: string): string {
    return `${first} ${last}`
  }

  private resetDialog() {
    this.selectedUser = null;
  }

  private filterUsers(title: string, queryText: string, item: any) {
    const user = item.raw;

    const searchableText = [user.first_name, user.last_name, user.email]
        .join(' ')
        .toLowerCase();
    const normalizedQuery = queryText.toLowerCase();

    return searchableText.includes(normalizedQuery);
  }

  private clearIfNoSelection() {
    this.$nextTick(() => {
      if (typeof this.selectedUser === 'string') {
        this.selectedUser = null;
      }
    });
  }

  private get filteredUsers() {
    const quicksightUserIds = new Set(this.quicksightUsers.map(u => u.id));

    return this.users
      .filter((user: User) => !quicksightUserIds.has(user.id))
      .sort((a: User, b: User) => {
        const nameA = a.first_name || '';
        const nameB = b.first_name || '';
        return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
      });
  }
}
</script>

<template>
  <div>
    <v-row justify="space-between" align="center" dense>
      <v-col cols="12">
        <v-row align="center" dense>
          <v-col cols="12">
            <v-card color="#F4F4F4" variant="flat" class="pa-2" height="48">
              <v-row dense align="center">
                <v-col cols="6" md="8">
                  <v-row no-gutters align="center">
                    <v-col cols="5" md="auto">
                      <v-dialog width="500" @update:modelValue="resetDialog()">
                        <template v-slot:activator="{ props }">
                          <v-btn v-bind="props" size="small" variant="plain" color="primary">
                            {{ mdAndUp ? 'Add quicksight user' : 'Add' }}
                          </v-btn>
                        </template>

                        <template v-slot:default="{ isActive }">
                          <v-card title="Add quicksight user">
                            <v-card-text>
                              <v-form>
                                <v-row>
                                  <v-col cols="12">
                                    <v-combobox
                                        class="app-combobox"
                                        clearable
                                        :custom-filter="filterUsers"
                                        :items="filteredUsers"
                                        :item-title="item => (typeof item === 'object' && item !== null) ? fullName(item.first_name, item.last_name) : item"
                                        item-value="id"
                                        hide-details
                                        density="comfortable"
                                        placeholder="Search users"
                                        return-object
                                        :menu-props="{ maxWidth: 452 }"
                                        v-model="selectedUser"
                                        @blur="clearIfNoSelection"
                                    >
                                      <template v-slot:selection="{ item }">
                                        <div class="d-flex flex-column">
                                          <template v-if="typeof item.raw === 'object' && item.raw !== null">
                                            <v-list-item-title>{{ fullName(item.raw.first_name, item.raw.last_name) }}</v-list-item-title>
                                            <v-list-item-subtitle>{{ item.raw.email }}</v-list-item-subtitle>
                                          </template>
                                          <template v-else>
                                            <v-list-item-title>{{ item.raw }}</v-list-item-title>
                                          </template>
                                        </div>
                                      </template>
                                      <template v-slot:item="{ props, item }">
                                        <div class="d-flex flex-column">
                                          <v-list-item v-bind="props" @click="selectedUser = item.raw">
                                            <v-list-item-subtitle>{{ item.raw.email }}</v-list-item-subtitle>
                                          </v-list-item>
                                        </div>
                                      </template>
                                    </v-combobox>
                                  </v-col>
                                </v-row>
                              </v-form>
                            </v-card-text>

                            <v-card-actions>
                              <v-spacer></v-spacer>

                              <v-btn
                                  :loading="loading"
                                  :disabled="!selectedUser?.id || loading"
                                  color="primary"
                                  @click="addQuicksightUser(); isActive.value = false"
                              >
                                Add Quicksight user
                              </v-btn>
                            </v-card-actions>
                          </v-card>
                        </template>
                      </v-dialog>
                    </v-col>
                    <v-col cols="5" md="auto">
                      <v-btn size="small" variant="plain" :disabled="!selectedUsers.length" color="primary" @click="removeQuicksightUser()">
                        {{ mdAndUp ? 'Remove quicksight user' : 'Remove' }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-data-table
        :items="quicksightUsers"
        :headers="headers"
        show-select
        return-object
        v-model="selectedUsers"
        height="calc(100vh - 260px)"
    >
      <template v-slot:[`item.status`]="{ item }">
        <div v-if="item.status === 'active'" class="d-flex">
          <v-icon start icon="mdi-circle" color="success"></v-icon>
          Active
        </div>
        <div v-if="item.status === 'pending'" class="d-flex">
          <v-icon start icon="mdi-circle" color="grey"></v-icon>
          Pending
        </div>
      </template>
      <template v-slot:[`item.full_name`]="{ item }">
        {{ fullName(item.first_name, item.last_name) }}
      </template>
    </v-data-table>
  </div>
</template>

<style scoped>

</style>
